import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { authorService, Author } from "../services/api";
import {
  Search,
  Filter,
  ChevronDown,
  User as UserIcon,
  TrendingUp,
  FileText,
  Star,
  UsersRound,
  Eye,
} from "lucide-react";
import toast from "react-hot-toast";

const Authors: React.FC = () => {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("mojo_desc");
  const [showFilters, setShowFilters] = useState(false);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadAuthors();
  }, [sortBy, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAuthors = async () => {
    try {
      setLoading(true);
      const response = await authorService.getAuthors(
        0,
        50, // Load more authors for better display
        sortBy,
        searchTerm || undefined
      );

      setAuthors(response.authors);
      setTotal(response.total);
    } catch (error) {
      toast.error("Error loading authors");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
    setShowFilters(false);
  };

  const getSortLabel = (sortValue: string) => {
    const labels = {
      mojo_desc: "Highest Rating",
      mojo_asc: "Lowest Rating",
      posts_desc: "Most articles",
      posts_asc: "Fewest articles",
      rating_desc: "Best rated",
      rating_asc: "Lowest rated",
    };
    return labels[sortValue as keyof typeof labels] || "Highest Rating";
  };

  const renderStarRating = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<Star key={i} className="w-4 h-4 fill-yellow-200 text-yellow-400" />);
      } else {
        stars.push(<Star key={i} className="w-4 h-4 text-gray-300" />);
      }
    }

    return (
      <div className="flex items-center space-x-1">
        <div className="flex">{stars}</div>
        <span className="text-sm text-gray-600 ml-1">{rating.toFixed(1)}</span>
      </div>
    );
  };

  return (
    <div
      className="authors-container w-full flex flex-row"
      style={{
        // paddingTop: "47px",
        paddingBottom: "47px",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "30px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          width: "100%",
          background: "rgba(0, 0, 0, 0)",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "17px",
          display: "flex",
          paddingTop: "17px",
          paddingBottom: "17px",
        }}
      >
        {/* Titulo Pagina */}
        {/* <div
          style={{
            alignSelf: "center",
            height: "44px",
            color: "#262626",
            fontSize: "36px",
            fontFamily: "Abhaya Libre",
            fontWeight: "400",
            wordWrap: "break-word",
          }}
        >
          Voices
        </div> */}
        <p
          style={{
            color: "#525252",
            fontSize: "18px",
            fontFamily: "Instrument Sans",
            fontWeight: "400",
            wordWrap: "break-word",
            textAlign: "center",
          }}
        >
          Meet the Voices of Beatpost - The writers, thinkers and storytellers shaping this community with their words.
        </p>
        {/* Separador horizontal */}
        <div
          className="hidden lg:block"
          style={{
            width: "100%",
            height: "1px",
            backgroundColor: "#D3D3D3",
          }}
        />
      </div>

      {/* Search and Filter Controls */}
      <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 md:max-w-[1009px] md:mx-auto">
        {/* Search Bar */}
        <div
          style={{
            paddingLeft: "16px",
            paddingRight: "16px",
            paddingTop: "9px",
            paddingBottom: "9px",
            background: "rgba(150.79, 15.95, 74.38, 0.05)",
            borderRadius: "49px",
            outline: "1.50px #97104A solid",
            outlineOffset: "-1.50px",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            display: "flex",
          }}
        >
          <div style={{ width: "18px", height: "18px", position: "relative", background: "rgba(0, 0, 0, 0)" }}>
            <div
              style={{
                width: "18px",
                height: "18px",
                left: "0px",
                top: "0px",
                position: "absolute",
                overflow: "hidden",
                justifyContent: "center",
                alignItems: "center",
                display: "inline-flex",
              }}
            >
              <Search style={{ width: "18px", height: "18px", color: "#97104A" }} />
            </div>
          </div>
          <input
            type="text"
            placeholder="Author search by keywords"
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#97104A",
              fontSize: "14px",
              fontFamily: "Abhaya Libre ExtraBold",
              fontWeight: "800",
              wordWrap: "break-word",
              width: "250px",
            }}
            className="placeholder-red"
          />
        </div>

        {/* Filter Controls */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingTop: "9px",
              paddingBottom: "9px",
              borderRadius: "49px",
              outline: "1px black solid",
              outlineOffset: "-1px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              display: "flex",
              background: "white",
              cursor: "pointer",
            }}
          >
            <span
              style={{
                color: "black",
                fontSize: "14px",
                fontFamily: "Abhaya Libre ExtraBold",
                fontWeight: "800",
                wordWrap: "break-word",
              }}
            >
              Sort by: {getSortLabel(sortBy)}
            </span>
            <div style={{ width: "12px", height: "12px", position: "relative", background: "rgba(0, 0, 0, 0)" }}>
              <ChevronDown
                style={{
                  width: "12px",
                  height: "12px",
                  color: "black",
                  transform: showFilters ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              />
            </div>
          </button>

          {showFilters && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: "0px",
                marginTop: "8px",
                width: "256px",
                background: "white",
                border: "1px solid #C2C2C2",
                borderRadius: "20px",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                zIndex: 10,
              }}
            >
              <div style={{ padding: "8px" }}>
                <button
                  onClick={() => handleSortChange("mojo_desc")}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    background: sortBy === "mojo_desc" ? "rgba(150.79, 15.95, 74.38, 0.05)" : "transparent",
                    color: sortBy === "mojo_desc" ? "#97104A" : "#525252",
                    fontFamily: "Abhaya Libre",
                    fontWeight: sortBy === "mojo_desc" ? "700" : "400",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <TrendingUp style={{ width: "16px", height: "16px", marginRight: "8px" }} />
                  Highest Rating
                </button>
                <button
                  onClick={() => handleSortChange("posts_desc")}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    background: sortBy === "posts_desc" ? "rgba(150.79, 15.95, 74.38, 0.05)" : "transparent",
                    color: sortBy === "posts_desc" ? "#97104A" : "#525252",
                    fontFamily: "Abhaya Libre",
                    fontWeight: sortBy === "posts_desc" ? "700" : "400",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FileText style={{ width: "16px", height: "16px", marginRight: "8px" }} />
                  Most published articles
                </button>
                <button
                  onClick={() => handleSortChange("rating_desc")}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    background: sortBy === "rating_desc" ? "rgba(150.79, 15.95, 74.38, 0.05)" : "transparent",
                    color: sortBy === "rating_desc" ? "#97104A" : "#525252",
                    fontFamily: "Abhaya Libre",
                    fontWeight: sortBy === "rating_desc" ? "700" : "400",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Star style={{ width: "16px", height: "16px", marginRight: "8px" }} />
                  Best rated
                </button>
              </div>
            </div>
          )}
        </div>

        {searchTerm && (
          <div
            style={{
              fontSize: "14px",
              color: "#525252",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              marginTop: "16px",
            }}
          >
            {authors.length} result{authors.length !== 1 ? "s" : ""} for "{searchTerm}"
          </div>
        )}
      </div>

      {/* Authors Grid */}
      {loading ? (
        <div className="loading beat-author-grid w-full flex flex-wrap items-start justify-center sm:justify-start md:justify-center gap-5 sm:gap-[27px] md:max-w-[1002px] md:mx-auto">
          {[...Array(6)].map((_, i) => (
            <div
              className="loading beat-author-card w-full sm:w-[316px]"
              key={i}
              style={{
                padding: "15px",
                borderRadius: "20px",
                outline: "1px #C2C2C2 solid",
                outlineOffset: "-1px",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: "15px",
                display: "inline-flex",
              }}
            >
              <div
                style={{
                  width: "286px",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: "18px",
                  display: "inline-flex",
                }}
              >
                <div
                  style={{
                    width: "76px",
                    height: "76px",
                    background: "#D9D9D9",
                    borderRadius: "10px",
                    animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                  }}
                />
                <div
                  style={{
                    width: "185px",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: "6px",
                    display: "inline-flex",
                  }}
                >
                  <div
                    style={{
                      height: "28px",
                      width: "120px",
                      background: "#D9D9D9",
                      borderRadius: "4px",
                      animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                    }}
                  />
                  <div
                    style={{
                      height: "20px",
                      width: "80px",
                      background: "#D9D9D9",
                      borderRadius: "4px",
                      animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                    }}
                  />
                </div>
              </div>
              <div
                style={{
                  height: "60px",
                  width: "100%",
                  background: "#D9D9D9",
                  borderRadius: "4px",
                  animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                }}
              />
            </div>
          ))}
        </div>
      ) : authors.length === 0 ? (
        <div
          className="No-authors beat-author-card w-full md:max-w-[1002px] md:mx-auto"
          style={{
            textAlign: "center",
            padding: "48px 0",
          }}
        >
          <UserIcon
            style={{
              width: "64px",
              height: "64px",
              margin: "0 auto 16px auto",
              color: "#A3A3A3",
            }}
          />
          <h3
            style={{
              fontSize: "20px",
              fontFamily: "Abhaya Libre",
              fontWeight: "600",
              color: "#262626",
              marginBottom: "8px",
              wordWrap: "break-word",
            }}
          >
            {searchTerm ? "No authors found" : "No authors available"}
          </h3>
          <p
            style={{
              color: "#525252",
              fontSize: "16px",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            {searchTerm ? `No authors match "${searchTerm}"` : "No authors are registered on the platform yet"}
          </p>
        </div>
      ) : (
        <div className="beat-author-grid w-full flex flex-wrap items-start justify-center sm:justify-start md:justify-center gap-5 sm:gap-[27px] md:max-w-[1002px] md:mx-auto">
          {authors.map((author) => (
            <div
              key={author.id}
              className="w-full sm:w-[316px] h-auto md:h-[222px] flex flex-col"
              style={{
                padding: "15px",
                borderRadius: "20px",
                outline: "1px #C2C2C2 solid",
                outlineOffset: "-1px",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "15px",
                display: "flex",
              }}
            >
              {/* Top Content Container */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                  width: "100%",
                }}
              >
                {/* Card Header */}
                <div className="beat-author-card-content-header w-full sm:w-[286px] flex items-center gap-[18px]">
                  {author.avatar ? (
                    <img
                      src={author.avatar}
                      alt={author.username}
                      style={{
                        width: "76px",
                        height: "76px",
                        background: "#D9D9D9",
                        borderRadius: "10px",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "76px",
                        height: "76px",
                        background: "#D9D9D9",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <UserIcon style={{ width: "32px", height: "32px", color: "#97104A" }} />
                    </div>
                  )}
                  <div className="w-full flex flex-col justify-start items-start gap-[6px]">
                    <div
                      style={{
                        color: "#262626",
                        fontSize: "18px",
                        fontFamily: "Abhaya Libre",
                        fontWeight: "400",
                        lineHeight: "28px",
                        wordWrap: "break-word",
                      }}
                    >
                      {author.username}
                    </div>
                    <div
                      className="beat-author-card-content-header-followers"
                      style={{
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "15px",
                        display: "inline-flex",
                      }}
                    >
                      {/* Mojo Count */}
                      <div
                        className="beat-author-card-content-header-mojo-count"
                        style={{
                          background: "rgba(0, 0, 0, 0)",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: "4px",
                          display: "flex",
                        }}
                      >
                        <img src="/mojo_icon.png" alt="Mojo" style={{ width: "16px", height: "16px" }} />

                        <div
                          style={{
                            width: "27px",
                            height: "20px",
                            color: "#737373",
                            fontSize: "14px",
                            fontFamily: "Abhaya Libre",
                            fontWeight: "400",
                            lineHeight: "20px",
                            wordWrap: "break-word",
                          }}
                        >
                          {Math.round(author.mojo)}
                        </div>
                      </div>
                      {/* Posts Count */}
                      <div
                        style={{
                          background: "rgba(0, 0, 0, 0)",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: "4px",
                          display: "flex",
                        }}
                      >
                        <FileText className="w-4 h-4" style={{ color: "#737373" }} />

                        <div
                          style={{
                            width: "26px",
                            height: "20px",
                            color: "#737373",
                            fontSize: "14px",
                            fontFamily: "Abhaya Libre",
                            fontWeight: "400",
                            lineHeight: "20px",
                            wordWrap: "break-word",
                          }}
                        >
                          {author.posts_count > 999 ? `${(author.posts_count / 1000).toFixed(1)}k` : author.posts_count}
                        </div>
                      </div>

                      {/* Followers Count */}
                      <div
                        style={{
                          background: "rgba(0, 0, 0, 0)",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          gap: "4px",
                          display: "flex",
                        }}
                      >
                        <UsersRound className="w-4 h-4" style={{ color: "#737373" }} />

                        <div
                          style={{
                            width: "26px",
                            height: "20px",
                            color: "#737373",
                            fontSize: "14px",
                            fontFamily: "Abhaya Libre",
                            fontWeight: "400",
                            lineHeight: "20px",
                            wordWrap: "break-word",
                          }}
                        >
                          {author.followers_count > 999
                            ? `${(author.followers_count / 1000).toFixed(1)}k`
                            : author.followers_count}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Bio */}
                <span
                  className="beat-author-card-content-bio"
                  style={{
                    maxWidth: "100%",
                    alignSelf: "stretch",
                    color: "#525252",
                    fontSize: "14px",
                    fontFamily: "Instrument Sans",
                    fontWeight: "400",
                    wordWrap: "break-word",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {author.bio
                    ? author.bio.length > 78
                      ? `${author.bio.substring(0, 74)} ...`
                      : author.bio
                    : "Sin biografía disponible"}
                </span>
              </div>

              {/* Card Buttons */}
              <Link
                to={`/voice/${author.username}`}
                className="w-full sm:w-auto"
                style={{
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  paddingTop: "9px",
                  paddingBottom: "9px",
                  borderRadius: "49px",
                  outline: "1px black solid",
                  outlineOffset: "-1px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  display: "inline-flex",
                  textDecoration: "none",
                }}
              >
                <div
                  style={{
                    color: "black",
                    fontSize: "14px",
                    fontFamily: "Abhaya Libre ExtraBold",
                    fontWeight: "800",
                    wordWrap: "break-word",
                  }}
                >
                  See profile
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Results Summary */}
      {!loading && authors.length > 0 && (
        <div className="mt-8 text-center text-newspaper-600">
          <p>
            Showing {authors.length} of {total} authors
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        </div>
      )}
    </div>
  );
};

export default Authors;
